import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SceneDef, SoloRunState, StoryElement } from '@plotjack/core';
import {
  getCardById,
  cardBeatCompatible,
  callbackLine,
  castDisplayName,
  castFor,
  castIsReturning,
  castShortRole,
  elementLabel,
  fillVars,
  narratorSceneEnd,
  sceneChips,
  CARD_TYPE_RISK,
} from '@plotjack/core';
import { BOOK1_STORY } from '../data/story';
import { SceneStage } from './SceneStage';
import { CardHand3D } from './three/CardHand3D';
import { NarratorPortrait } from './NarratorPortrait';
import { PlayCardBar } from './PlayCardBar';
import { DraftWizard } from './DraftWizard';
import { EndSceneConfirm } from './EndSceneConfirm';
import { TutorialOverlay, type TutorialStep } from './TutorialOverlay';
import { QuestPanel } from './QuestPanel';
import { StoryLog } from './StoryLog';
import { TurnSteps, type TurnStep } from './TurnSteps';
import { EndingMeters } from './EndingMeters';
import { WhatChanged } from './WhatChanged';
import { SceneSummary } from './SceneSummary';
import { targetLayer, targetUv } from '../art/anchors';
import { figuresFor } from '../art/figures';
import { audio } from '../audio/engine';
import {
  evaluatePlay,
  firstCompatibleBeatIndex,
  hasPlayableCard,
  playDisabledMessage,
} from '../lib/playCardUi';

interface Props {
  state: SoloRunState;
  scene: SceneDef;
  narratorBody: string;
  onBeginScene: () => void;
  onPlayCard: (cardId: string, beatId: string) => void;
  onEndScene: () => void;
  onDraft: (choice: 'take' | 'upgrade' | 'burn' | 'skip', id?: string) => void;
  tutorialStep: TutorialStep | null;
  tutorialShowNext: boolean;
  onTutorialNext: () => void;
  onTutorialSkip: () => void;
  onTutorialEvent: (event: 'card_selected' | 'target_selected' | 'card_played' | 'draft_phase') => void;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Wrap every story target mentioned in a line in a <mark> (a button while you can aim cards). */
function highlightTargets(
  text: string,
  elements: { el: StoryElement; label: string; index: number }[],
  opts: { focused?: number; valid?: (i: number) => boolean; onPick?: (i: number) => void },
): ReactNode {
  const needles = elements
    .flatMap((e) => [e.label, e.el.label].map((n) => ({ n, e })))
    .filter((x) => x.n.length > 2)
    .sort((a, b) => b.n.length - a.n.length);
  if (!needles.length) return text;
  const re = new RegExp(`(${needles.map((x) => escapeRe(x.n)).join('|')})`, 'gi');
  return text.split(re).map((part, i) => {
    const hit = needles.find((x) => x.n.toLowerCase() === part.toLowerCase());
    if (!hit) return <Fragment key={i}>{part}</Fragment>;
    const idx = hit.e.index;
    const cls = `target-mark ${opts.focused === idx ? 'target-mark--focus' : ''} ${opts.valid && !opts.valid(idx) ? 'target-mark--dim' : ''}`;
    return opts.onPick ? (
      <button key={i} type="button" className={`${cls} target-mark--btn`} onClick={() => opts.onPick!(idx)} aria-label={`Target ${part}`}>
        {part}
      </button>
    ) : (
      <mark key={i} className={cls}>
        {part}
      </mark>
    );
  });
}

export function GameScreen({
  state,
  scene,
  narratorBody,
  onBeginScene,
  onPlayCard,
  onEndScene,
  onDraft,
  tutorialStep,
  tutorialShowNext,
  onTutorialNext,
  onTutorialSkip,
  onTutorialEvent,
}: Props) {
  const story = BOOK1_STORY;
  const vars = useMemo(() => ({ ...(story.variables ?? {}), ...state.variables }), [story.variables, state.variables]);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [focusedBeat, setFocusedBeat] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [summarySeen, setSummarySeen] = useState(-1);
  const glitch = state.narratorGlitchActive;

  const outcomeText = state.lastOutcome?.bridgeText ?? '';
  const quip = state.lastOutcome?.narratorQuip ?? '';
  const history = state.history ?? [];
  const sceneChanges = history.filter((c) => c.sceneIndex === state.sceneIndex);
  const lastChange = state.phase === 'scene_play' && state.lastOutcome ? sceneChanges[sceneChanges.length - 1] : undefined;
  const planStatus = sceneChanges.some((c) => c.plan === 'blocked')
    ? 'blocked'
    : sceneChanges.some((c) => c.plan === 'delayed')
      ? 'delayed'
      : sceneChanges.length >= 2
        ? 'bent'
        : 'stands';
  const endEntry = state.phase === 'draft' ? (state.sceneLog ?? []).find((e) => e.sceneIndex === state.sceneIndex) : undefined;
  const endReaction = endEntry ? narratorSceneEnd(endEntry) : undefined;
  const bgId = scene.backgroundReuseId ?? scene.id;

  // Type the narration in over time (not per frame, so slow phones finish on schedule). The full text and the
  // narrator's plan are laid out from the start and only revealed, so nothing below (the Respond button)
  // shifts under a finger while it types.
  useEffect(() => {
    if (state.phase === 'scene_intro') {
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        setDisplayText(narratorBody);
        return;
      }
      setDisplayText('');
      const full = narratorBody;
      const start = performance.now();
      const CPS = 110;
      let raf = 0;
      const tick = (now: number) => {
        const n = Math.min(full.length, Math.ceil(((now - start) / 1000) * CPS));
        setDisplayText(full.slice(0, n));
        if (n < full.length) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }
  }, [state.phase, state.sceneIndex, narratorBody]);

  useEffect(() => {
    setSelectedCard(null);
    setFocusedBeat(0);
    setEndConfirmOpen(false);
  }, [state.sceneIndex, state.phase]);

  // Narrator voice: the scene narration as it opens, then the reaction once the scene wraps.
  useEffect(() => {
    if (state.phase === 'scene_intro') {
      audio.playSfx('page_turn');
      void audio.playLine(narratorBody);
    }
  }, [state.phase, state.sceneIndex, narratorBody]);
  useEffect(() => {
    if (endReaction?.line) void audio.playLine(endReaction.line);
  }, [endReaction?.line]);
  useEffect(() => () => audio.stopVoice(), []);

  // Sound for the hand and the meters.
  const prevRef = useRef({ hand: state.hand.length, dread: state.dread, tracks: { ...state.tracks } });
  useEffect(() => {
    const prev = prevRef.current;
    if (state.hand.length > prev.hand) audio.playSfx('card_draw', { volume: 0.8 });
    if (state.dread > prev.dread) window.setTimeout(() => audio.playSfx('meter_down'), 650);
    const rose = (['hope', 'chaos', 'fate'] as const).some((t) => (state.tracks[t] ?? 0) > (prev.tracks[t] ?? 0));
    if (rose) window.setTimeout(() => audio.playSfx('meter_up', { volume: 0.7 }), 900);
    prevRef.current = { hand: state.hand.length, dread: state.dread, tracks: { ...state.tracks } };
  }, [state.hand.length, state.dread, state.tracks]);

  const beats = scene.beats;
  const focusedBeatId = beats[focusedBeat]?.id ?? null;
  const selectedDef = selectedCard ? getCardById(selectedCard) : null;

  const beatEls = useMemo(
    () =>
      beats.map((b, index) => {
        const el = scene.elements.find((e) => e.id === b.elementId) ?? scene.elements[0];
        return { el, label: elementLabel(el, story, vars), index, beat: b };
      }),
    [beats, scene.elements, story, vars],
  );

  const targetLabel = beatEls[focusedBeat]?.label;

  const playCheck = evaluatePlay(state, scene, story, selectedCard, focusedBeatId);
  const disabledReason = selectedCard ? playDisabledMessage(playCheck.reason) : 'Select a card and a story target.';

  const tryPlay = useCallback(() => {
    if (!selectedCard || !focusedBeatId || !playCheck.ok) return;
    audio.stopVoice();
    audio.playSfx('card_fly');
    onPlayCard(selectedCard, focusedBeatId);
    onTutorialEvent('card_played');
    setSelectedCard(null);
  }, [selectedCard, focusedBeatId, playCheck.ok, onPlayCard, onTutorialEvent]);

  const handleSelectCard = useCallback(
    (id: string) => {
      setSelectedCard(id);
      setFocusedBeat(firstCompatibleBeatIndex(scene, id));
      onTutorialEvent('card_selected');
    },
    [scene, onTutorialEvent],
  );

  const handleSelectBeat = useCallback(
    (index: number) => {
      setFocusedBeat(index);
      onTutorialEvent('target_selected');
    },
    [onTutorialEvent],
  );

  useEffect(() => {
    if (state.phase === 'draft') onTutorialEvent('draft_phase');
  }, [state.phase, onTutorialEvent]);

  const playable = hasPlayableCard(state, scene, story);
  const requestEndScene = useCallback(() => {
    if (state.energy > 0 && playable) {
      setEndConfirmOpen(true);
      return;
    }
    onEndScene();
  }, [state.energy, playable, onEndScene]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (logOpen) return;
      if (state.phase === 'scene_intro' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onBeginScene();
      }
      if (state.phase === 'scene_play') {
        if (e.key === 'ArrowRight' || e.key === 'Tab') {
          e.preventDefault();
          handleSelectBeat((focusedBeat + 1) % beats.length);
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handleSelectBeat((focusedBeat - 1 + beats.length) % beats.length);
        }
        if (e.key === 'Enter' && playCheck.ok && selectedCard) {
          e.preventDefault();
          tryPlay();
        }
        if (e.key === 'Escape') setSelectedCard(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.phase, beats.length, onBeginScene, selectedCard, tryPlay, playCheck.ok, focusedBeat, handleSelectBeat, logOpen]);

  const beatCompatible = (beatIndex: number) => {
    if (!selectedDef) return true;
    const beat = beats[beatIndex];
    return beat ? cardBeatCompatible(selectedDef, beat) : false;
  };

  // ---------- what to show where ----------
  const log = state.sceneLog ?? [];
  const finishedEntry = state.phase === 'draft' ? log[log.length - 1] : undefined;
  const showSummary = state.phase === 'draft' && !!finishedEntry && summarySeen !== state.sceneIndex;
  const nextScene = story.scenes[state.sceneIndex + 1];

  let step: TurnStep = 'announce';
  let prompt = 'Read what is happening and the narrator\u2019s plan, then tap Respond with a card.';
  if (state.phase === 'scene_play') {
    if (selectedCard) {
      step = 'respond';
      prompt = playCheck.ok
        ? `Aiming ${selectedDef?.name} at ${targetLabel}. Tap Play card, or pick a different highlighted target.`
        : disabledReason;
    } else if (sceneChanges.length) {
      step = 'outcome';
      prompt = playable
        ? 'Read What changed below. Play another card, or tap End scene when you are done.'
        : 'Out of Ink or matching cards. Tap End scene to see how the scene played out.';
    } else {
      step = 'respond';
      prompt = playable
        ? 'Pick a card from your hand to answer the narrator\u2019s plan.'
        : 'No card fits this scene. Tap End scene to let it play out.';
    }
  } else if (state.phase === 'draft') {
    step = 'next';
    prompt = showSummary ? 'Scene wrapped. Check what you changed, then continue.' : 'Choose one change to your deck for the next scene, or skip.';
  }

  const previously = scene.previously ? fillVars(scene.previously, vars) : '';
  const callback = callbackLine(history, scene, state.sceneIndex);
  const intentText = fillVars(scene.intent, vars);
  const chips = sceneChips(history, scene, state.sceneIndex);
  const cast = beatEls.filter((b) => b.el.beatType === 'character' && castFor(story, b.el.id));

  const focusTarget = state.phase === 'scene_play' && selectedCard ? beatEls[focusedBeat] : undefined;
  const hitTarget = lastChange ? beatEls.find((b) => b.el.id === lastChange.elementId) : undefined;
  const pinned = focusTarget ?? hitTarget;
  const pinUv = pinned ? targetUv(scene.id, pinned.el.id, pinned.el.beatType) : undefined;
  const lastDelta = lastChange
    ? { dreadMeter: lastChange.dreadDelta, ...lastChange.trackDeltas }
    : undefined;

  const highlight = (text: string, interactive: boolean) =>
    highlightTargets(text, beatEls, {
      focused: selectedCard ? focusedBeat : undefined,
      valid: selectedDef ? beatCompatible : undefined,
      onPick: interactive && state.phase === 'scene_play' ? handleSelectBeat : undefined,
    });

  return (
    <div className="game-layout">
      <TutorialOverlay step={tutorialStep} onSkip={onTutorialSkip} onNext={onTutorialNext} showNext={tutorialShowNext} />
      <EndSceneConfirm
        open={endConfirmOpen}
        inkLeft={state.energy}
        onCancel={() => setEndConfirmOpen(false)}
        onConfirm={() => {
          setEndConfirmOpen(false);
          onEndScene();
        }}
      />
      <StoryLog open={logOpen} story={story} log={log} current={{ title: scene.title, goal: scene.goal }} onClose={() => setLogOpen(false)} />

      <QuestPanel story={story} scene={scene} sceneIndex={state.sceneIndex} onOpenLog={() => setLogOpen(true)} logCount={log.length} />
      <EndingMeters state={state} story={story} pulse={lastDelta} />
      <TurnSteps step={step} prompt={prompt} />

      <SceneStage
        sceneId={bgId}
        mood={state.sceneMood}
        glitch={glitch}
        overlayIds={state.lastOutcome?.visual.overlayIds}
        playKey={state.lastOutcome ? `${state.sceneIndex}-${state.playedThisScene.length}-${state.lastOutcome.key}` : undefined}
        playedCardId={state.playedThisScene[state.playedThisScene.length - 1]?.cardId}
        dread={state.dread}
        dramatic={state.lastOutcome?.tier === 'hero'}
        targetUv={hitTarget ? targetUv(scene.id, hitTarget.el.id, hitTarget.el.beatType) : undefined}
        targetLayer={hitTarget ? targetLayer(scene.id, hitTarget.el.id) : undefined}
        pin={pinned && pinUv ? { uv: pinUv, label: pinned.label, hit: !focusTarget, layer: targetLayer(scene.id, pinned.el.id) } : undefined}
        chips={chips}
        figures={figuresFor(scene.id)}
      />

      <section className="panel narrator-panel" aria-live="polite">
        <NarratorPortrait
          expression={state.phase === 'scene_play' ? state.lastOutcome?.visual.narratorExpression : endReaction?.expression}
          glitch={glitch}
        />
        <div className={`narrator-text ${glitch ? 'narrator-text--glitch' : ''}`}>
          <p className="scene-title">{scene.title}</p>
          {state.phase === 'scene_intro' && (
            <>
              {previously && (
                <p className="previously">
                  <span className="quest-label">Previously</span> {previously}
                </p>
              )}
              {callback && <p className="callback">{callback}</p>}
              <p className="narration" aria-label={narratorBody} onClick={() => setDisplayText(narratorBody)}>
                <span aria-hidden="true">{displayText}</span>
                <span className="narration__rest" aria-hidden="true">{narratorBody.slice(displayText.length)}</span>
              </p>
              <p className={`intent intent--announce ${displayText.length >= narratorBody.length ? '' : 'intent--pending'}`} aria-hidden={displayText.length < narratorBody.length}>
                <span className="intent__label">The narrator announces</span> {intentText}
              </p>
            </>
          )}
          {state.phase === 'scene_play' && (
            <>
              <p className="narration narration--play">{highlight(narratorBody, true)}</p>
              <p className={`intent intent--${planStatus}`}>
                <span className="intent__label">
                  {planStatus === 'blocked' ? 'Plan blocked' : planStatus === 'delayed' ? 'Plan delayed' : planStatus === 'bent' ? 'Plan bent' : "Narrator's plan"}
                </span>{' '}
                {planStatus === 'stands' ? highlight(intentText, true) : <del>{intentText}</del>}
              </p>
            </>
          )}
          {endReaction && endEntry && (
            <>
              <blockquote className="narrator-reaction">
                <span className="intent__label">The narrator</span> "{fillVars(endReaction.line, vars)}"
              </blockquote>
            </>
          )}
          {cast.length > 0 && state.phase !== 'draft' && (
            <ul className="cast-tags" aria-label="Who is here">
              {cast.map((c) => {
                const role = castFor(story, c.el.id);
                const name = castDisplayName(c.el.id, story, vars);
                const returning = castIsReturning(state.metCast, c.el.id);
                return (
                  <li key={c.el.id} className="cast-tag">
                    <strong>
                      {name}
                      {role ? `, ${castShortRole(role.role)}` : ''}
                    </strong>
                    <span>
                      {returning ? 'Returns' : 'First appearance'}
                      {role ? ` · ${role.role}` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

          {planStatus === 'blocked' && state.phase === 'scene_play' && (
        <p className="plan-blocked-banner" role="status">
          Counter played: the narrator&apos;s plan is blocked. It will not add Dread when you end the scene.
        </p>
      )}

      {lastChange && (
        <WhatChanged change={lastChange} scene={scene} story={story} state={state} bridge={outcomeText} quip={quip} />
      )}

      {state.phase === 'scene_intro' && (
        <button type="button" className="btn btn-primary btn-respond" onClick={onBeginScene}>
          Respond with a card
        </button>
      )}

      {state.phase === 'scene_play' && (
        <>
          <div className="beats" role="toolbar" aria-label="Story targets">
            {beatEls.map(({ el, label, index, beat }) => {
              const incompatible = selectedDef && !beatCompatible(index);
              const role = castFor(story, el.id)?.role;
              return (
                <button
                  key={beat.id}
                  type="button"
                  className={`beat-btn ${index === focusedBeat && selectedCard ? 'beat-btn--focus' : ''} ${incompatible ? 'beat-btn--dim' : ''} ${selectedDef && beatCompatible(index) ? 'beat-btn--valid' : ''}`}
                  onClick={() => handleSelectBeat(index)}
                  aria-pressed={index === focusedBeat && !!selectedCard}
                >
                  {label}
                  <span className="beat-type">{role ? role.split(/[,;]/)[0] : beat.beatType}</span>
                </button>
              );
            })}
          </div>
          <CardHand3D
            hand={state.hand}
            selectedId={selectedCard}
            onSelect={handleSelectCard}
            onPlay={(id) => {
              if (id === selectedCard && focusedBeatId && playCheck.ok) tryPlay();
            }}
            energy={state.energy}
            canPlaySelected={playCheck.ok}
          />
          <PlayCardBar
            cardName={selectedDef?.name}
            cardCost={selectedDef?.cost}
            cardDescription={selectedDef?.description}
            cardRisk={selectedDef ? CARD_TYPE_RISK[selectedDef.type] : undefined}
            targetLabel={selectedCard ? targetLabel : undefined}
            canPlay={playCheck.ok}
            disabledReason={disabledReason}
            onPlay={tryPlay}
            onEndScene={requestEndScene}
          />
        </>
      )}

      {showSummary && finishedEntry && (
        <SceneSummary
          entry={finishedEntry}
          story={story}
          next={nextScene ? { title: nextScene.title, previously: nextScene.previously ? fillVars(nextScene.previously, vars) : undefined } : undefined}
          onContinue={() => setSummarySeen(state.sceneIndex)}
        />
      )}
      {state.phase === 'draft' && !showSummary && <DraftWizard state={state} onDraft={onDraft} />}
    </div>
  );
}
